import {
  FILTER_OPERATIONS,
  IAppliedFilterValue,
} from 'components/Grids/DataGrid/Filters/utils/types';
import { DELIMITER } from 'utils/filters';
import { IAllPageFilters, INullable } from 'utils/types';

import { ANALYTIC_FILTER_OPERATIONS } from './constant';
import { TBulkFilter, TFilter } from './types';

export const nullableFilter: TFilter = {
  fieldName: null,
  operation: null,
  value: null,
};

export const nullableBulkFilter: TFilter = {
  fieldName: null,
  operation: null,
  value: [null],
};

export const LIKE_OPERATORS = [
  ANALYTIC_FILTER_OPERATIONS.LIKE,
  ANALYTIC_FILTER_OPERATIONS.NOT_LIKE,
];
export const MULTISELECT_OPERATORS = [
  ANALYTIC_FILTER_OPERATIONS.IN,
  ANALYTIC_FILTER_OPERATIONS.NOT_IN,
];

export enum REQUEST_FILTERS {
  ADAPTER_CODE = 'adapterCode',
  BANK_NAME = 'bankName',
  BIN = 'bin',
  BIN_COUNTRY = 'binCountry',
  BROWSER_NAME = 'browserName',
  BROWSER_VERSION = 'browserVersion',
  CARD_BRAND = 'cardBrand',
  CARD_ISSUING_ORGANIZATION = 'cardIssuingOrganization',
  CASHIER_PAYMENT_METHOD_NAME = 'cashierPaymentMethodName',
  COUNTRY = 'country',
  CURRENCY = 'currency',
  DEVICE_TYPE = 'deviceType',
  FULL_BIN = 'fullBin',
  IS_MOTO = 'isMoto',
  IS_TRUSTED = 'isTrusted',
  MERCHANT_NAME = 'merchantName',
  MERCHANT_TERMINAL_NAME = 'merchantTerminalName',
  OPERATING_SYSTEM = 'operatingSystem',
  OPERATION_STATE = 'operationState',
  OPERATION_TYPE = 'operationType',
  PAYMENT_METHOD = 'paymentMethod',
  PAYMENT_TYPE = 'paymentType',
  RESULT_CODE = 'resultCode',
  TERMINAL_LINK_NAME = 'terminalLinkName',
  TRANSACTION_STATE = 'transactionState',
  TRANSACTION_STATUS = 'transactionStatus',
  USER_BANK_MESSAGE = 'userBankMessage',
}

export const REQUEST_TRANSACTION_FILTERS = {
  [REQUEST_FILTERS.ADAPTER_CODE]: REQUEST_FILTERS.ADAPTER_CODE,
  [REQUEST_FILTERS.BANK_NAME]: REQUEST_FILTERS.BANK_NAME,
  [REQUEST_FILTERS.BIN]: REQUEST_FILTERS.BIN,
  [REQUEST_FILTERS.BIN_COUNTRY]: REQUEST_FILTERS.BIN_COUNTRY,
  [REQUEST_FILTERS.BROWSER_NAME]: REQUEST_FILTERS.BROWSER_NAME,
  [REQUEST_FILTERS.CARD_BRAND]: REQUEST_FILTERS.CARD_BRAND,
  [REQUEST_FILTERS.CARD_ISSUING_ORGANIZATION]: REQUEST_FILTERS.CARD_ISSUING_ORGANIZATION,
  [REQUEST_FILTERS.CASHIER_PAYMENT_METHOD_NAME]:
    REQUEST_FILTERS.CASHIER_PAYMENT_METHOD_NAME,
  [REQUEST_FILTERS.COUNTRY]: REQUEST_FILTERS.COUNTRY,
  [REQUEST_FILTERS.CURRENCY]: REQUEST_FILTERS.CURRENCY,
  [REQUEST_FILTERS.DEVICE_TYPE]: REQUEST_FILTERS.DEVICE_TYPE,
  [REQUEST_FILTERS.FULL_BIN]: REQUEST_FILTERS.FULL_BIN,
  [REQUEST_FILTERS.MERCHANT_NAME]: REQUEST_FILTERS.MERCHANT_NAME,
  [REQUEST_FILTERS.MERCHANT_TERMINAL_NAME]: REQUEST_FILTERS.MERCHANT_TERMINAL_NAME,
  [REQUEST_FILTERS.OPERATING_SYSTEM]: REQUEST_FILTERS.OPERATING_SYSTEM,
  [REQUEST_FILTERS.OPERATION_STATE]: REQUEST_FILTERS.OPERATION_STATE,
  [REQUEST_FILTERS.OPERATION_TYPE]: REQUEST_FILTERS.OPERATION_TYPE,
  [REQUEST_FILTERS.PAYMENT_METHOD]: REQUEST_FILTERS.PAYMENT_METHOD,
  [REQUEST_FILTERS.RESULT_CODE]: REQUEST_FILTERS.RESULT_CODE,
  [REQUEST_FILTERS.TERMINAL_LINK_NAME]: REQUEST_FILTERS.TERMINAL_LINK_NAME,
  [REQUEST_FILTERS.TRANSACTION_STATE]: REQUEST_FILTERS.TRANSACTION_STATE,
  [REQUEST_FILTERS.TRANSACTION_STATUS]: REQUEST_FILTERS.TRANSACTION_STATUS,
  [REQUEST_FILTERS.USER_BANK_MESSAGE]: REQUEST_FILTERS.USER_BANK_MESSAGE,
};

export const NOT_DICTIONARY_FIELDS = [
  REQUEST_FILTERS.USER_BANK_MESSAGE,
  REQUEST_FILTERS.CASHIER_PAYMENT_METHOD_NAME,
  REQUEST_FILTERS.DEVICE_TYPE,
  REQUEST_FILTERS.BROWSER_NAME,
  REQUEST_FILTERS.OPERATING_SYSTEM,
];

export enum FILTER_OPERATORS {
  EQUAL = 'EQUAL',
  GREATER_THAN = 'GREATER_THAN',
  IN = 'IN',
  LESS_THAN = 'LESS_THAN',
  LIKE = 'LIKE',
  NOT_EQUAL = 'NOT_EQUAL',
  NOT_IN = 'NOT_IN',
  NOT_LIKE = 'NOT_LIKE',
}

export const REQUEST_TRANSACTION_OPERATORS = {
  [FILTER_OPERATORS.EQUAL]: ':',
  [FILTER_OPERATORS.GREATER_THAN]: '>',
  [FILTER_OPERATORS.IN]: '#',
  [FILTER_OPERATORS.LESS_THAN]: '<',
  [FILTER_OPERATORS.LIKE]: '~',
  [FILTER_OPERATORS.NOT_EQUAL]: '!',
  [FILTER_OPERATORS.NOT_IN]: '!#',
  [FILTER_OPERATORS.NOT_LIKE]: '!~',
};

interface IGetMainProps {
  operation: null | string | undefined;
}

export const getMainProps = ({ operation }: IGetMainProps) => {
  if (MULTISELECT_OPERATORS.some((operator) => operator === operation)) {
    return {
      disableCloseOnSelect: true,
      disableUnselect: false,
      inputReadOnly: false,
      maxVisibleMultiselectItems: 1,
      multiselect: true,
    };
  }

  return {};
};

export const getNormalizeFilters = (
  nullableFilters: TFilter,
  filters?: INullable<TFilter[]>,
): INullable<TBulkFilter[] | TFilter[]> => {
  if (!filters?.length) {
    return [];
  }

  if (Array.isArray(nullableFilters?.value)) {
    return (
      filters
        ?.filter((filter) => JSON.stringify(filter) !== JSON.stringify(nullableFilters))
        .map((item) => ({
          fieldName: item.fieldName,
          operation: item.operation,
          value: (item.value as (string | string[])[])
            .filter((value) => !!value && !!value.length)
            .map((value) =>
              Array.isArray(value)
                ? value.filter((value) => !!value).join(DELIMITER)
                : value,
            ),
        })) || null
    );
  }

  return (
    filters
      ?.filter((filter) => JSON.stringify(filter) !== JSON.stringify(nullableFilters))
      .map((item) => ({
        fieldName: item.fieldName,
        operation: item.operation,
        value: Array.isArray(item.value)
          ? item.value.filter((value) => !!value).join(DELIMITER)
          : item.value,
      })) || null
  );
};

export const getStructuredRequestFilters = ({
  dateFrom,
  dateTo,
  filters,
}: {
  dateFrom: string;
  dateTo: string;
  filters: INullable<TFilter[]>;
}): IAllPageFilters => {
  const requestFilters = filters
    ?.filter((item) => !!item.value?.toString())
    .reduce(
      (acc, item) => {
        const { fieldName, operation, value } = item;

        if (fieldName && !(Array.isArray(value) && Array.isArray(value[0]))) {
          acc[fieldName] = {
            operation: REQUEST_TRANSACTION_OPERATORS[
              operation as keyof typeof REQUEST_TRANSACTION_OPERATORS
            ] as FILTER_OPERATIONS,
            value: value as never,
          };
        }

        return acc;
      },
      {} as Record<string, IAppliedFilterValue | undefined>,
    );

  return {
    search: {
      created: {
        operation: FILTER_OPERATIONS.RANGE,
        value: {
          max: dateTo,
          min: dateFrom,
        },
      },
      ...requestFilters,
    },
  };
};
